import { DatePicker, DatePickerProps, LocalizationProvider } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterDayjs';
import { TextField } from '@mui/material';
import useHelperTextId from '@platform-ui-common/hooks/contexts/useHelperTextId.js';
import { getLabelProps } from '@platform-ui/utilities/application/utils.js';
import { fixCjsModule } from '@platform-ui/utilities/esm.js';
import cx from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEvent, useState } from 'react';
import Color from '../../utilities/Color.js';
import Icon from '../Icon/index.js';

export interface DateInputProps extends Pick<DatePickerProps, 'onError'> {
  autoComplete?: string;
  disabled?: boolean;
  error?: string | boolean;
  fullWidth?: boolean;
  helperText?: string;
  id: string;
  label: string;
  labelPrefix?: string;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  onChange: (value: string) => void;
  readonly?: boolean;
  required?: boolean;
  value: string;
}

export default function DateInput({
  autoComplete,
  disabled,
  error,
  fullWidth,
  helperText,
  id,
  labelPrefix,
  maxDate,
  minDate,
  onChange,
  readonly,
  required,
  ...rest
}: DateInputProps): JSX.Element {
  const [newDate, setNewDate] = useState('');
  const [invalid, setInvalid] = useState(false);

  function OpenPickerIcon(): JSX.Element {
    return <Icon type="calendar" color={disabled ? Color.DisabledLabel : Color.Text} size="MD" />;
  }

  const onAccept = (value: string): void => {
    const newYear = new Date(newDate).getFullYear();
    const year = new Date(value as string).getFullYear();
    const sameYear = newYear === year;

    onChange(dayjs(sameYear || !newDate ? value : newDate).format('MM/DD/YYYY'));

    setNewDate('');
  };

  const handleChange = (value: string): void => {
    if (!required && !value) {
      setNewDate('');
      onChange('');

      return;
    }

    if (invalid) {
      setNewDate('invalid');
      onChange('invalid');

      return;
    }
    const date = dayjs(value).format('MM/DD/YYYY');
    if (date !== 'Invalid Date') {
      setNewDate(value);
      onChange(date);
    }
  };
  const helperTextId = useHelperTextId();

  return (
    <LocalizationProvider dateAdapter={fixCjsModule(DateAdapter)}>
      <DatePicker
        {...rest}
        components={{ OpenPickerIcon }}
        onChange={(value: string | Dayjs): void => handleChange(value as string)}
        disabled={disabled}
        readOnly={readonly}
        onAccept={(value: string): void => {
          onAccept(value);
        }}
        renderInput={(params): JSX.Element => {
          const label = labelPrefix ? `${labelPrefix} ${params.label}` : params.label;
          const inputId = labelPrefix ? `${id}-${labelPrefix.split(' ').join('-')}` : id;
          return (
            <div className={cx('max-w-full', { 'w-field': !fullWidth })}>
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  'aria-errormessage': helperTextId,
                  'aria-describedby': helperTextId,
                }}
                FormHelperTextProps={{
                  id: helperTextId,
                }}
                label={label}
                id={inputId}
                onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
                  const { value } = event.target;
                  if (value.includes('00/00/0000') || value === '0' || value === '00') {
                    setInvalid(true);
                  } else {
                    setInvalid(!dayjs(value).isValid());
                  }
                }}
                required={required}
                variant="filled"
                error={!!error}
                helperText={typeof error === 'string' ? error : helperText}
                autoComplete={autoComplete}
                fullWidth
                disabled={disabled}
                InputLabelProps={getLabelProps(!!readonly, rest.value)}
              />
            </div>
          );
        }}
        minDate={minDate}
        maxDate={maxDate}
        value={rest.value ?? ''}
      />
    </LocalizationProvider>
  );
}
